import { Pane, PaneHeader, PaneScrollable, PaneWrapper } from '@campfire/pane';
import { TabletButton } from '@campfire/tablet-button/lib';
import { TitularTooltip } from '@campfire/titular-tooltip';
import { Box, Grid, IconButton } from '@material-ui/core';
import { Add, Clear } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { ReportTypeFormValues } from './manage-report-type-model';
import { useManageReportTypeContext } from './ManageReportTypeContext';
import { ReportTypeForm } from './ReportTypeForm';
import { ReportTypeSideNav } from './ReportTypeSideNav';

const SIDE_NAV_WIDTH = 320;

const ManageReportTypePage = () => {
  const {
    reportTypeIsLoading,
    selectedReportTypeId,
    setSelectedReportTypeId,
    reportTypes,
    reportTypesListIsLoading,
    setCreationModeOn,
    saveReportIsLoading,
  } = useManageReportTypeContext();

  const formik = useFormikContext<ReportTypeFormValues>();
  const { theme } = useCampfireTheme();

  const [hasChanges, setHasChanges] = useState(false);
  useEffect(() => {
    if (hasChanges) return;
    setHasChanges(formik.values !== formik.initialValues);
  }, [formik.values, formik.initialValues]);

  return (
    <PaneWrapper>
      {reportTypesListIsLoading ? (
        <ManageReportTypeSkeletonLoading />
      ) : (
        <Grid container style={{ overflowX: 'hidden', flexWrap: 'nowrap', height: '100%' }}>
          <Grid item style={{ display: 'flex', flex: '0 1 auto' }}>
            <Pane style={{ width: SIDE_NAV_WIDTH, maxWidth: SIDE_NAV_WIDTH, minWidth: SIDE_NAV_WIDTH }}>
              <PaneHeader>
                <Box paddingTop={3} paddingBottom={1} paddingX={2}>
                  {/* Sidebar Header */}
                  <TabletButton
                    endIcon={<Add />}
                    color='primary'
                    variant='text'
                    onClick={() => {
                      setSelectedReportTypeId(undefined);
                      setCreationModeOn(true);
                    }}
                  >
                    {'New Report Type'}
                  </TabletButton>
                </Box>
              </PaneHeader>
              <PaneScrollable>
                <Box>
                  <ReportTypeSideNav />
                </Box>
              </PaneScrollable>
            </Pane>
          </Grid>
          <Grid
            item
            xs
            style={{ display: 'flex', flex: '1 1 auto', borderLeft: `1px solid ${theme.color.grey.border}` }}
          >
            <Pane>
              <PaneHeader>
                <Box
                  display='flex'
                  width={1}
                  justifyContent='space-between'
                  alignContent='center'
                  alignItems='center'
                  paddingX={2}
                  paddingY={1}
                  borderBottom={`1px solid ${theme.color.grey.border}`}
                  style={{ boxSizing: 'border-box' }}
                >
                  <Box display='inline-block'>
                    <TabletButton
                      type='submit'
                      variant='contained'
                      color='primary'
                      style={{ marginRight: 8 }}
                      disabled={saveReportIsLoading}
                    >
                      {'Save Report Type'}
                    </TabletButton>
                    {hasChanges && formik.values.reportTypeId ? (
                      <TabletButton
                        variant='text'
                        onClick={() => {
                          formik.resetForm();
                          setHasChanges(false);
                        }}
                      >
                        {'Discard Changes'}
                      </TabletButton>
                    ) : null}
                  </Box>

                  <TitularTooltip description='Close Window'>
                    <Box>
                      <Link to='/management/activities/timeline' style={{ textDecoration: 'none' }}>
                        <IconButton>
                          <Clear />
                        </IconButton>
                      </Link>
                    </Box>
                  </TitularTooltip>
                </Box>
              </PaneHeader>
              <PaneScrollable>
                <Box position='relative' display='flex' flex='1 1 auto' height={1}>
                  {reportTypeIsLoading ? (
                    <Box paddingX={3} paddingY={2}>
                      <Skeleton variant='text' width={350} height={25} />
                      <Skeleton variant='text' width={200} height={15} />
                    </Box>
                  ) : (
                    <ReportTypeForm
                      selectedReportType={reportTypes.find(
                        (reportType) => reportType.reportTypeId === selectedReportTypeId
                      )}
                    />
                  )}
                </Box>
              </PaneScrollable>
            </Pane>
          </Grid>
        </Grid>
      )}
    </PaneWrapper>
  );
};

const ManageReportTypeSkeletonLoading = () => {
  const { theme } = useCampfireTheme();
  return (
    <Box display='flex' flex='1 1 auto' height={1} width={1}>
      <Box paddingLeft={4} paddingTop={3} width={SIDE_NAV_WIDTH} maxWidth={SIDE_NAV_WIDTH} minWidth={SIDE_NAV_WIDTH}>
        <Skeleton variant='text' width={150} height={25} />
        <Skeleton style={{ marginTop: 55 }} variant='text' width={'80%'} height={30} />

        <Box paddingTop={10}>
          <Skeleton variant='text' width={200} height={10} />
          <Skeleton variant='text' width={150} height={10} />
        </Box>
        <Box paddingTop={2}>
          <Skeleton variant='text' width={200} height={10} />
          <Skeleton variant='text' width={150} height={10} />
        </Box>
      </Box>
      <Box display='flex' flex='1 1 auto' borderLeft={`1px solid ${theme.color.grey.border}`} width={1}>
        <Box width={1}>
          <Box paddingX={3} paddingY={1} borderBottom={`1px solid ${theme.color.grey.border}`} display='flex'>
            <Skeleton variant='text' width={150} height={25} style={{ marginRight: 16 }} />
            <Skeleton variant='text' width={150} height={25} style={{ marginRight: 16 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export { ManageReportTypePage };
